import {AxiosError} from "axios";
import {useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {Button} from "@/components/ui/button";
import {Loader} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {getAxiosErrorMessage} from "@/src/modules/core/lib";
import {captureLocationRequiredFailure} from "@/src/modules/core/lib/glitchtip/device-capture";
import {resolveFeatureFromWindowLocation} from "@/src/modules/core/lib/glitchtip/resolve-feature";
import {useSessionEndStore} from "@/src/modules/auth/store/session-end";
import {useStoredSession} from "@/src/modules/auth/store/session";

interface AuthErrorHandlerProps {
  error: Error | AxiosError;
  resetErrorBoundary: (...args: any[]) => void;
}

export default function AuthErrorHandler({error, resetErrorBoundary}: AuthErrorHandlerProps) {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useStoredSession();
  const sessionEnding = useSessionEndStore((s) => s.sessionEnding);

  const isAuthError = error instanceof AxiosError && error.response?.status === 401;
  const isLocationRequiredError =
    (error instanceof AxiosError && error.response?.data?.code === "location_required") ||
    (error.cause instanceof GeolocationPositionError && error.cause.code === 1);

  useEffect(() => {
    if (sessionEnding || (!session && isAuthError)) {
      resetErrorBoundary();
      return;
    }

    let message = "";

    if (isAuthError) {
      if (location.pathname === "/login") {
        message = "Invalid credentials";
      } else {
        resetErrorBoundary();
        return;
      }
    } else if (error instanceof AxiosError) {
      [message] = getAxiosErrorMessage(error);
    } else {
      message = error.message;
    }

    if (message) {
      toast.error(message);
    }
  }, [error, isAuthError, location.pathname, resetErrorBoundary, session, sessionEnding, toast]);

  useEffect(() => {
    if (sessionEnding) {
      resetErrorBoundary();
      return;
    }

    if (isAuthError) {
      if (location.pathname === "/login") {
        resetErrorBoundary();
        return;
      }
      resetErrorBoundary();
      return;
    }

    if (isLocationRequiredError) {
      captureLocationRequiredFailure(resolveFeatureFromWindowLocation());
      navigate("/location-denied");
      resetErrorBoundary();
    }
  }, [
    isAuthError,
    isLocationRequiredError,
    location.pathname,
    navigate,
    resetErrorBoundary,
    sessionEnding,
  ]);

  if (sessionEnding || (isAuthError && !session)) {
    return null;
  }

  if (isAuthError || isLocationRequiredError) {
    return <Loader />;
  }

  return (
    <div className="h-[100vh] w-full flex flex-col items-center justify-center gap-4 mt-auto mb-auto">
      <p className="text-2xl font-bold text-slate-700">Oops! Something went wrong.</p>
      <div className="flex flex-col ">
        <Button
          onClick={() => {
            resetErrorBoundary();
            window.location.reload();
          }}
        >
          Reload the page
        </Button>
        <Button
          variant="link"
          className="text-xs text-muted-foreground"
          onClick={() => {
            navigate("/");
            resetErrorBoundary();
          }}
        >
          Or return to the home
        </Button>
      </div>
    </div>
  );
}
